import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker } from "@/components/Reveal";
import { Field, TextArea, Select, SubmitButton } from "@/components/FormBits";
import { submitConsultation } from "@/lib/api";

const INVITED = [
  "Aboriginal Elders", "Aboriginal Community Controlled Organisations", "Aboriginal young people",
  "Parents", "Kinship carers", "Foster carers", "People with lived experience of care",
  "Communities", "Schools", "Aboriginal education workers", "Youth workers",
  "Narrative practitioners", "Child protection professionals", "NGOs", "Prospective referral partners",
];

const OPPORTUNITIES = [
  "Join a Community Yarn", "Participate in Young Person Consultation", "Join Carer Consultation",
  "Partner on Program Design", "Attend a Professional Roundtable", "Discuss a Pilot",
  "Contribute Community Knowledge",
];

const AREAS = [
  "Program design", "Finding the Threads", "Cultural guidance", "Placement stability",
  "Education engagement", "Family connection & reunification", "Movement & fitness",
  "Community programs", "Evaluation & research", "Something else",
];

const FORMATS = [
  "Individual yarn", "Community yarning circle", "Young-person consultation", "Carer consultation",
  "Aboriginal community consultation", "Professional roundtable", "Online consultation", "Pilot co-design",
];

const EMPTY = { name: "", organisation: "", contact: "", submitter_email: "", connection: "", area: "", format: "", perspective: "", accessibility: "", anything_else: "" };

const Consultation = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) {
      toast.error("Please add your name and contact details.");
      return;
    }
    setBusy(true);
    try {
      await submitConsultation(form);
      toast.success("Thank you — your expression of interest has been received. We'll be in touch to yarn.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong sending that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="consultation-page">
      <PageHero
        kicker="Community Consultation & Co-design"
        title={"Built with community,\nnot simply delivered to community."}
        lede="Ngawilyi Narrative is being shaped through ongoing consultation, accountability and learning — not tokenistic endorsement. Your knowledge, experience and perspective will directly shape how this model works."
      />

      <section className="px-6 py-14 sm:py-20 bg-cream">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Kicker>Who we're inviting</Kicker>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
              Help Shape <span className="italic text-terracotta">Ngawilyi.</span>
            </h2>
            <div className="mt-8 flex flex-wrap gap-3">
              {INVITED.map((g) => (
                <span key={g} className="rounded-full bg-creamdark border border-ochre/35 px-4 py-2 text-[13px] font-semibold text-earth">{g}</span>
              ))}
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4">
            {OPPORTUNITIES.map((o, i) => (
              <Reveal key={o} delay={i * 0.05}>
                <div data-testid={`opportunity-${i}`} className="rounded-3xl bg-blush/40 border border-blush px-6 py-6 h-full transition-transform duration-300 hover:-translate-y-1">
                  <span className="font-display italic text-terracotta text-lg">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-1 font-semibold text-charcoal leading-snug">{o}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-24 bg-cream">
        <Reveal>
          <div className="max-w-4xl mx-auto rounded-[48px] bg-creamdark border border-ochre/25 p-8 sm:p-14 grain relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-terracotta/10 organic-blob blur-2xl" />
            <div className="relative">
              <Kicker>Expression of Interest</Kicker>
              <h2 className="font-display font-light text-3xl sm:text-4xl text-charcoal">Start the conversation.</h2>
              <p className="mt-4 text-earth/85 leading-relaxed">
                Tell us a little about yourself and how you'd like to be involved. There are no wrong answers —
                this is a yarn, not an application.
              </p>
              <form data-testid="consultation-form" onSubmit={onSubmit} className="mt-10 grid sm:grid-cols-2 gap-6">
                <Field label="Name" testid="eoi-name-input" value={form.name} onChange={set("name")} required />
                <Field label="Organisation / community" testid="eoi-organisation-input" optional value={form.organisation} onChange={set("organisation")} />
                <Field label="Contact details" testid="eoi-contact-input" value={form.contact} onChange={set("contact")} required placeholder="Email or phone" />
                <Field label="Your email — for a confirmation copy" testid="eoi-email-input" type="email" optional value={form.submitter_email} onChange={set("submitter_email")} />
                <Field label="Connection to this work" testid="eoi-connection-input" optional value={form.connection} onChange={set("connection")} placeholder="e.g. kinship carer, teacher, young person" />
                <Select label="Area of consultation" testid="eoi-area-select" optional options={AREAS} value={form.area} onChange={set("area")} />
                <Select label="Preferred consultation format" testid="eoi-format-select" optional options={FORMATS} value={form.format} onChange={set("format")} />
                <div className="sm:col-span-2">
                  <TextArea label="Community, lived or professional perspective you'd like to share" testid="eoi-perspective-textarea" optional value={form.perspective} onChange={set("perspective")} />
                </div>
                <Field label="Accessibility needs" testid="eoi-accessibility-input" optional value={form.accessibility} onChange={set("accessibility")} />
                <div className="sm:col-span-2">
                  <TextArea label="Anything else you would like Ngawilyi to know" testid="eoi-anything-textarea" optional value={form.anything_else} onChange={set("anything_else")} />
                </div>
                <div className="sm:col-span-2 pt-2">
                  <SubmitButton testid="eoi-submit-btn" busy={busy}>Send Expression of Interest</SubmitButton>
                </div>
              </form>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Consultation;
